// @flow
import { useMemo, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import {
	ClientSideRowModelModule,
	ExcelExportModule,
	LicenseManager,
	ColumnsToolPanelModule,
	FiltersToolPanelModule,
	MenuModule, RowGroupingModule, ServerSideRowModelModule,
	SetFilterModule,
	MasterDetailModule,
} from '@ag-grid-enterprise/all-modules';
import { getData, updateData } from '@shared/utils/localStorage';
import { snackActions } from '@shared/helper/toster';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns-jalali';
import { useI18n } from '@shared/helper/hooks';
import findNestedValue from '@shared/utils/findNestedValue';
import type { agGridType } from '@shared/model/components/agGrid';
// import DownloadToolPanel from './components/downloadToolPanel';
import CustomDateComponent from './components/dateFilter';
import '@ag-grid-enterprise/all-modules/dist/styles/ag-grid.css';
import '@ag-grid-enterprise/all-modules/dist/styles/ag-theme-balham.css';
import './styles.scss';

function AgGrid({ service, serviceParams, commandParams, columns, name, dataKey, totalRecordKey, updateDependency=[], ...props }: agGridType, ref): React$Node {
	const { translate } = useI18n('finoShared');

	LicenseManager.setLicenseKey('CompanyName=Fino,LicensedGroup=Multi,LicenseType=MultipleApplications,[v25.3.0]_MTYyNDU2MzcwMDc5NA==07e3cb93ef26d535e10d092cc654b423');

	const modules = useMemo(() => [
		ClientSideRowModelModule,
		ServerSideRowModelModule,
		RowGroupingModule,
		ColumnsToolPanelModule,
		FiltersToolPanelModule,
		ExcelExportModule,
		SetFilterModule,
		MenuModule,
		MasterDetailModule,
	], []);

	const [gridApi, setGridApi] = useState(null);
	const [gridColumnApi, setGridColumnApi] = useState(null);

	const [searchParams, setSearchParams] = useSearchParams();
	const pageSize = Number(searchParams.get('pageSize')) || getData(name)?.pageSize || 5;

	useEffect(() => {
		const { page, ...previousSearchParams } = Object.fromEntries(searchParams);
		setSearchParams({
			...previousSearchParams,
		});
	}, [searchParams, setSearchParams])

	// config default column for all columns
	const defaultColDef = useMemo(() => ({
		flex: 1,
		minWidth: 140,
		sortable: true,
		filter: false,
		unSortIcon: true,
		resizable: true,
		wrapText: true,
		menuTabs: ['generalMenuTab', 'filterMenuTab'],
		filterParams: {
			suppressAndOrCondition: true,
			debounceMs: 500,
		},
	}), []);

	function normalizeColumnDef(columnDef = []) {
		return columnDef.map(item => {
			const { filter, dropdownValues, ...column } = item;
			if (filter === 'dateFilter') {
				return {
					...column,
					filter: 'agDateColumnFilter',
					filterParams: {
						filterOptions: ['lessThan', 'greaterThan', 'equals'],
						defaultOption: 'equals',
						...column?.filterParams,
					},
				};
			}
			if (filter === 'text') {
				return {
					...column,
					filter: 'agTextColumnFilter',
					filterParams: {
						filterOptions: ['equals', 'contains'],
						defaultOption: 'contains',
						...item?.filterParams,
					},
				};
			}
			if (filter === 'number') {
				return {
					...column,
					filter: 'agNumberColumnFilter',
					filterParams: {
						filterOptions: ['equals', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual', 'contains'],
						defaultOption: 'contains',
						...column?.filterParams,
					},
				};
			}
			if (filter === 'dropdown') {
				return {
					...column,
					filter: 'agSetColumnFilter',
					filterParams: {
						values: dropdownValues,
						defaultToNothingSelected: true,
						suppressSelectAll: true,
						valueFormatter(r) {
							return r.value.split(',')[0];
						},
						...column?.filterParams,
					},
				};
			}
			return { ...item };
		});
	}

	const columnDefs = normalizeColumnDef(columns);
	const mainRowHeight = 67;
	const localeText = {
		// Set Filter
		selectAll: 'انتخاب همه',
		selectAllSearchResults: 'انتخاب همه نتایج جستجو',
		searchOoo: 'جستجو...',
		blanks: 'خالی',
		noMatches: 'موردی یافت نشد.',

		// Number Filter & Text Filter
		filterOoo: 'فیلتر',
		// فیلتر یجورایی تو فارسی جا افتاده.
		equals: 'برابر است با',
		notEqual: 'برابر نیست با',
		empty: 'یک مورد انتخاب کنید.',

		// Number Filter
		lessThan: 'کوچکتر از',
		greaterThan: 'بزرگتر از',
		lessThanOrEqual: 'کوچکتر یا مساوی با',
		greaterThanOrEqual: 'بزرگتر یا مساوی با',
		inRange: 'در محدوده',
		inRangeStart: 'به',
		inRangeEnd: 'از',

		// Text Filter
		contains: 'شامل میشود',
		notContains: 'شامل نمیشود',
		startsWith: 'شروع میشود با',
		endsWith: 'پایان مییابد با',

		// Date Filter
		dateFormatOoo: 'قالب تاریخ',

		// Filter Conditions
		andCondition: 'و',
		orCondition: 'یا',

		// Filter Buttons
		applyFilter: 'ثبت',
		resetFilter: 'تنظیم مجدد',
		clearFilter: 'حذف',
		cancelFilter: 'انصراف',

		// Filter Titles
		textFilter: 'فیلتر متنی',
		numberFilter: 'فیلتر عددی',
		dateFilter: 'فیلتر زمانی',
		setFilter: 'تنطیم فیلتر',

		// Side Bar
		columns: translate('columns'),
		filters: 'فیلترها',

		// columns tool panel
		pivotMode: 'حالت محوری',
		groups: 'گروه ردیف‌ها',
		rowGroupColumnsEmptyMessage: 'برای تنظیم ردیف‌ها به اینجا بکشید.',
		// برای ایجاد ردیف جدید به اینجا بکشید.
		values: 'مقادیر',
		valueColumnsEmptyMessage: 'برای جمع‌آوری به اینجا بکشید.',
		pivots: 'سرستون‌ها',
		pivotColumnsEmptyMessage: 'برای تنظیم سرستون‌ها به اینجا بکشید',
		// برای ایجاد عنوان ستون جدید به اینجا بکشید.

		// Header of the Default Group Column
		group: 'گروه',

		// Other
		loadingOoo: 'درحال بارگذاری...',
		noRowsToShow: 'ردیفی برای نمایش موجود نیست.',
		enabled: 'فعال',

		// Menu
		pinColumn: 'سنجاق کردن ستون',
		// گرچه بنظرم پین کردن خودش جا افتاده.
		pinLeft: 'سنجاق کردن به چپ',
		pinRight: 'سنجاق کردن به راست',
		noPin: 'حذف سنجاق',
		valueAggregation: 'جمع مقادیر',
		autosizeThiscolumn: 'تغییر اندازه خودکار این ستون',
		autosizeAllColumns: 'تغییر اندازه خودکار همه ستون‌ها',
		groupBy: 'دسته‌بندی براساس',
		ungroupBy: 'حذف دسته‌بندی براساس',
		resetColumns: 'تنظیم مجدد ستون‌ها',
		expandAll: 'باز کردن همه',
		collapseAll: 'بستن همه',
		copy: 'کپی',
		// رونوشت :/
		ctrlC: 'کپی',
		copyWithHeaders: 'کپی با عنوان‌ها',
		paste: 'الصاق',
		// پیست
		ctrlV: 'الصاق',
		export: 'گرفتن خروجی',
		csvExport: 'CSV گرفتن خروجی',
		excelExport: 'گرفتن خروجی اکسل',

		// Enterprise Menu Aggregation and Status Bar
		sum: 'جمع',
		min: 'حداقل',
		max: 'حداکثر',
		none: 'هیچ',
		count: 'شمارش',
		avg: 'میانگین',
		filteredRows: 'فیلتر شده',
		selectedRows: 'انتخاب شده',
		totalRows: 'جمع تمام ردیف‌ها',
		totalAndFilteredRows: 'ردیف‌ها',
		more: 'بیشتر',
		// نمایش بیشتر
		to: 'تا',
		of: 'از',
		page: 'صفحه',
		nextPage: 'صفحه بعد',
		lastPage: 'صفحه آخر',
		firstPage: 'صفحه اول',
		previousPage: 'صفحه قبلی',

		// Enterprise Menu (Charts)
		pivotChartAndPivotMode: 'نمودار و حالت محوری',
		pivotChart: 'نمودار محوری',
		chartRange: 'محدوده نمودار',

		columnChart: 'ستون',
		groupedColumn: 'گروه‌بندی شده',
		stackedColumn: 'دسته‌بندی شده',
		normalizedColumn: 'کاملا دسته‌بندی شده',

		barChart: 'میله',
		groupedBar: 'گروه‌بندی شده',
		stackedBar: 'دسته‌بندی شده',
		normalizedBar: 'کاملا دسته‌بندی شده',

		pieChart: 'نمودار دایره‌ای',
		pie: 'دایره‌ای',
		// پای
		doughnut: 'دوناتی',
		// دونات

		line: 'خطی',
		// خط

		xyChart: 'پراکندگی خطی',
		// نمودار اگه نقطه‌ای باشه میانگین همه داده هارو بدست میارن بعد یه خط میکشن تو نمودار این اونه
		scatter: 'پراکندگی',
		// پراکنده
		bubble: 'حبابی',
		// حباب

		areaChart: 'نمودار محدوده‌ای',
		area: 'محدوده‌ای',
		// محدوده
		// منطقه
		stackedArea: 'دسته‌بندی شده',
		normalizedArea: 'کاملا دسته‌بندی شده',

		histogramChart: 'نمودار میله‌ای',

		// Charts
		pivotChartTitle: 'نمودار محورس',
		rangeChartTitle: 'محدوده نمودار',
		settings: 'تنظیمات',
		data: 'داده',
		format: 'قالب',
		categories: 'دسته‌بندی‌ها',
		defaultCategory: 'انتخاب نشده',
		series: 'رده‌ها',
		xyValues: 'مقادیر خطی',
		// عین scatter
		paired: 'حالت جفتی',
		// حالت دوتایی
		axis: 'محور',
		navigator: 'سمت‌یابی',
		color: 'رنگ',
		thickness: 'ضخامت',
		xType: 'نوع افقی',
		// اگه مربوط به نمودار نباشه x مجهوله
		automatic: 'خودکار',
		category: 'دسته‌بندی',
		number: 'عدد',
		time: 'زمان',
		xRotation: 'چرخش افقی',
		yRotation: 'چرخش عمودی',
		ticks: 'Ticks',
		width: 'عرض',
		height: 'ارتفاع',
		length: 'طول',
		padding: 'حاشیه',
		spacing: 'ایجاد فضا',
		chart: 'نمودار',
		title: 'عنوان',
		titlePlaceholder: 'عنوان نمودار - برای تغییر دوبار کلیک نمایید',
		background: 'پس‌زمینه',
		font: 'فونت',
		top: 'بالا',
		right: 'راست',
		bottom: 'پایین',
		left: 'چپ',
		labels: 'برچسب‌‌ها',
		size: 'اندازه',
		minSize: 'حداقل اندازه',
		maxSize: 'حداکثر اندازه',
		legend: 'توضیحات',
		// راهنمای نمودار
		position: 'مکان',
		markerSize: 'اندازه نشانگر',
		markerStroke: 'ضخامت نشانگر',
		markerPadding: 'حاشیه نشانگر',
		itemSpacing: 'فضای مورد',
		itemPaddingX: 'حاشیه افقی مورد',
		itemPaddingY: 'حاشیه عمودی مورد',
		layoutHorizontalSpacing: 'فضاسازی افقی',
		layoutVerticalSpacing: 'فضاسازی عمودی',
		strokeWidth: 'عرض ضخامت',
		offset: 'فاصله از کنار',
		offsets: 'فاصله‌ها از کنار',
		// یه گپ از هرطرف چارت که از جلوتر شروع بشه یا عقبتر تموم بشه
		tooltips: 'راهنماها',
		callout: 'Callout',
		markers: 'نشانگرها',
		// هر دیتا میشه یه مارکر احتمالا
		shadow: 'سایه',
		blur: 'تار',
		xOffset: 'فاصله افقی',
		yOffset: 'فاصله عمودی',
		lineWidth: 'عرض خط',
		normal: 'استاندارد',
		bold: 'بولد',
		italic: 'ایتالیک',
		boldItalic: 'بولد و ایتالیک',
		predefined: 'از قبل تعریف شده',
		fillOpacity: 'پر کردن ظرفیت',
		strokeOpacity: 'ظرفیت خط',
		histogramBinCount: 'طول بازه نمودار میله‌ای',
		columnGroup: 'گروه ستونی',
		barGroup: 'گروه میله‌ای',
		pieGroup: 'گروه دایره‌ای',
		lineGroup: 'گروه خطی',
		scatterGroup: 'پراکندگی خطی',
		areaGroup: 'گروه محدوده‌ای',
		histogramGroup: 'گروه میله‌ای',
		groupedColumnTooltip: 'راهنمای ستون گروه‌بندی شده',
		stackedColumnTooltip: 'راهنمای ستون دسته‌بندی شده',
		normalizedColumnTooltip: 'راهنمای ستون کاملا دسته‌بندی شده',
		groupedBarTooltip: 'راهنمای مبله گروه‌بندی شده',
		stackedBarTooltip: 'راهنمای مبله دسته‌بندی شده',
		normalizedBarTooltip: 'راهنمای مبله کاملا دسته‌بندی شده',
		pieTooltip: 'راهنمای دایره‌ای',
		doughnutTooltip: 'راهنمای دوناتی',
		lineTooltip: 'راهنمای خطی',
		groupedAreaTooltip: 'راهنمای محدوده گروه‌بندی شده',
		stackedAreaTooltip: 'راهنمای محدوده دسته‌بندی شده',
		normalizedAreaTooltip: 'راهنمای محدوده کاملا دسته‌بندی شده',
		scatterTooltip: 'راهنمای پراکندگی',
		bubbleTooltip: 'راهنمای حبابی',
		histogramTooltip: 'راهنمای میله‌ای',
		noDataToChart: 'هیچ داده‌ای برای نمودار در دسترس نیست.',
		pivotChartRequiresPivotMode: 'نمودار محوری به فعالسازی حالت محوری نیاز دارد.',
		chartSettingsToolbarTooltip: 'فهرست',
		// منو
		chartLinkToolbarTooltip: 'وصل شده به ردیف',
		// لینک شده به گرید :)
		chartUnlinkToolbarTooltip: 'جداشده از ردیف',
		chartDownloadToolbarTooltip: 'دانلود نمودار',

		// ARIA
		ariaHidden: 'مخفی',
		ariaVisible: 'قابل مشاهده',
		ariaChecked: 'انتخاب شده',
		ariaUnchecked: 'انتخاب نشده',
		ariaIndeterminate: 'نامشخص',
		ariaColumnSelectAll: 'نمایش/عدم نمایش همه ستون‌های انتخاب شده',
		ariaInputEditor: 'ویرایشگر ورودی',
		ariaDateFilterInput: 'ورودی فیلتر تاریخ',
		ariaFilterInput: 'ورودی فیلتر',
		ariaFilterColumnsInput: 'ورودی ستون‌های فیلتر',
		ariaFilterValue: 'مقدار فیلتر',
		ariaFilterFromValue: 'فیلتر کردن با استفاده از مقدار',
		ariaFilterToValue: 'فیلتر برای مقداردهی',
		ariaFilteringOperator: 'عامل فیلتر',
		// انجام دهنده فیلتر
		ariaColumnToggleVisibility: 'وضعیت مشاهده نمایش/عدم نمایش ستون',
		ariaColumnGroupToggleVisibility: 'وضعیت مشاهده نمایش/عدم نمایش  گروه ستون‌ها',
		ariaRowSelect: 'برای انتخاب این ردیف، دکمه اسپیس را بزنید.',
		ariaRowDeselect: 'برای عدم انتخاب این ردیف، دکمه اسپیس را بزنید.',
		ariaRowToggleSelection: 'برای تغییر وضعیت انتخاب ردیف، دکمه اسپیس را بزنید.',
		ariaRowSelectAll: 'برای تغییر وضعیت انتخاب همه ردیف‌ها، دکمه اسپیس را بزنید.',
		ariaSearch: 'جستجو',
		ariaSearchFilterValues: 'مقادیر جستجو',
		// مقادیر جستجوی فیلتری,
	};
	const icons = {
		'download': '<span class="ag-icon ag-icon-save"></span>',
	};
	const sideBar = {
		toolPanels: [
			{
				id: 'columns',
				labelDefault: 'Columns',
				labelKey: 'columns',
				iconKey: 'columns',
				toolPanel: 'agColumnsToolPanel',
				toolPanelParams: {
					suppressRowGroups: true,
					suppressValues: true,
					suppressPivots: true,
					suppressPivotMode: true,
					suppressColumnFilter: true,
					suppressColumnSelectAll: true,
					suppressColumnExpandAll: true,
				},
			},
			// {
			// 	id: 'download',
			// 	labelDefault: translate('download'),
			// 	labelKey: 'download',
			// 	iconKey: 'download',
			// 	toolPanelFramework: DownloadToolPanel,
			// },
		],
	};

	const frameworkComponents = useMemo(() => ({ agDateInput: CustomDateComponent }), []);

	if (!service) {
		console.error('WARNING - grid service for AG Grid not found. Please ensure the attribute ag-grid points to a valid object on the scope');
	}

	function sortQueryStringHandler(sortArray) {
		if (sortArray.length > 0) {
			const firstSort = sortArray[0];
			return `${firstSort.colId}~${firstSort.sort}`;
		}
		return null;

	}

	function filterModelHandler(filterModel) {
		const searchInfo = {};

		// eslint-disable-next-line no-restricted-syntax
		for (const [index, item] of Object.entries(filterModel).entries()) {
			// eslint-disable-next-line prefer-const
			let [fieldName, filter] = item;

			if ((filter.filterType === 'set' && filter.values.length === 0) ||
				(filter.filterType === 'text' && !filter.filter?.trim())
			) {
				// eslint-disable-next-line no-continue
				continue;
			}

			if (filter.filterType === 'set' && filter.values.length > 0) {
				filter = {
					type: 'equals',
					filter: filter.values.map(filterItem => filterItem.split(',')[1]).toString(),
					filterType: 'text',
				};
			}

			if (filter.filterType === 'date' && filter.dateFrom.length > 0) {
				filter = {
					type: filter.type || 'equals',
					filter: format(new Date(filter.dateFrom), 'yyyy/MM/dd'),
					filterType: 'date',
				};
			}

			searchInfo[`searchInfo[${index}].fieldName`] = fieldName;
			searchInfo[`searchInfo[${index}].operator`] = filter.type;
			searchInfo[`searchInfo[${index}].fieldValue`] = filter.filterType === 'text' ? filter.filter?.trim() : filter.filter;
			searchInfo[`searchInfo[${index}].type`] = filter.filterType;
		}

		return searchInfo;
	}

	function getGridData(params) {

		const { api } = params;

		const requestInfo = params.request;
		// console.log({ requestInfo });

		// console.log('paginationGetPageSize', pageSize);

		const currentPage = api.paginationGetCurrentPage() + 1;
		// console.log('paginationGetCurrentPage', currentPage);


		const sorts = sortQueryStringHandler(requestInfo.sortModel);
		// console.log('sorts', sorts);

		// const info = `Getting datasource rows, start: ${  requestInfo.startRow  }, end: ${  requestInfo.endRow}`;

		const searchInfo = filterModelHandler(requestInfo.filterModel);
		// console.log('searchInfo', searchInfo);

		const gridQuery = {
			sorts,
			page: currentPage,
			pageSize,
			...searchInfo,
		};

		const query = { ...serviceParams, ...gridQuery };
		api.hideOverlay();

		service(query, { ...commandParams }).then(
			(response) => {

				const rowData = findNestedValue(response, dataKey) ?? [];
				const totalRecord = totalRecordKey ? findNestedValue(response, totalRecordKey) ?? 0 : rowData.length;

				params.success({
					rowData,
					rowCount: totalRecord,
				});
				// // console.log(currentPage,page);
				// if (page && page !== currentPage) {
				//   console.log(page);
				//   console.log(page-1);
				//   api.paginationGoToPage(page - 1);
				// }

				if (rowData.length === 0) {
					api.showNoRowsOverlay();
				}
			},
			errResponse => {
				// params.fail();
				params.success({
					rowData: [],
					rowCount: 0,
				});
				api.showNoRowsOverlay();
				snackActions.error(errResponse.message || errResponse.data.message || translate('failedToGetInformation'));
			},
		);
	}

	function initGrid(api) {
		const dataSource = {
			getRows: (params) => {
				getGridData(params);
			},
		};
		api.setServerSideDatasource(dataSource);
	}

	useEffect(() => {
		if (gridApi) {
			// Force new cache block size by resetting internal cache
			// gridApi.paginationSetPageSize(pageSize);
			gridApi.gridOptionsWrapper.setProperty('cacheBlockSize', pageSize);
			initGrid(gridApi);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams.get('pageSize'), ...updateDependency]);

	function onGridSizeChanged(params) {
		params.api.sizeColumnsToFit();
	}

	function onFirstDataRendered(params) {
		params.api.sizeColumnsToFit();
	}

	function onGridColumnsChanged(params) {
		params.api.sizeColumnsToFit();
	}

	function onColumnVisible(params) {
		const columnState = params.columnApi.getColumnState().map(state => ({
			colId: state.colId,
			hide: state.hide,
		}));
		updateData(name, 'columnState', columnState);
	}

	function onToolPanelVisibleChanged(params) {
		params.api.sizeColumnsToFit();
	}

	/*
	 تابع زیر بخاطر باگ INVEST-1986 کامنت شد
	 همچنین بالاتر، useEffect وجود دارد که با هر تغییرِ سرچ پارامز،
	 پارامتر پیج رو از مجموع پارامترها خارج میکند و بقیه رو ست میکند
	 با این توصیفات فعلا آورده ای برای پروژه‌های‌ ما نداشت
	 * */

	// const onPaginationChanged = (event) => {
	// 	const currentPage = event.api.paginationGetCurrentPage() + 1;
	// 	if (event.newPage) {
	// 		const previousSearchParams = Object.fromEntries(searchParams);
	// 		setSearchParams({
	// 			...previousSearchParams,
	// 			page: currentPage,
	// 			pageSize,
	// 		});
	// 	}
	// }

	function onGridReady(params) {
		const { columnState = [] } = getData(name) || {};
		params.columnApi.applyColumnState({ state: columnState });
		setGridApi(params.api);
		setGridColumnApi(params.columnApi);
		initGrid(params.api);
	}

	useImperativeHandle(ref, () => ({
		refetch: () => initGrid(gridApi),
		getGridApi: () => gridApi,
		getGridColumnApi: () => gridColumnApi,
	}));

	return (
		<AgGridReact
			// bug with rtl
			// reactUi={'true'}
			className={'ag-theme-balham ag-theme-fino'}
			modules={modules}
			columnDefs={columnDefs}
			defaultColDef={defaultColDef}
			onGridReady={(params) => onGridReady(params)}
			// onPaginationChanged={onPaginationChanged}
			cacheBlockSize={pageSize}
			paginationPageSize={pageSize}
			rowModelType={'serverSide'}
			serverSideStoreType={'partial'}
			domLayout={'autoHeight'}
			overlayNoRowsTemplate={'<span>متاسفانه موردی یافت نشد.</span>'}
			rowHeight={mainRowHeight}
			localeText={localeText}
			icons={icons}
			sideBar={sideBar}
			frameworkComponents={frameworkComponents}
			animateRows
			pagination
			enableRtl
			groupDisplayType={'custom'}
			suppressDragLeaveHidesColumns
			loadingCellRenderer={undefined}
			suppressContextMenu={!props?.getContextMenuItems}
			allowContextMenuWithControlKey
			suppressRowClickSelection
			onToolPanelVisibleChanged={(params) => onToolPanelVisibleChanged(params)}
			onColumnVisible={(params) => onColumnVisible(params)}
			onGridColumnsChanged={(params) => onGridColumnsChanged(params)}
			onFirstDataRendered={(params) => onFirstDataRendered(params)}
			onGridSizeChanged={(params) => onGridSizeChanged(params)}
			{...props}
		/>
	);
}

export default forwardRef(AgGrid);

